import * as d3 from "d3";
import moment from "moment";
import { observer } from "mobx-react-lite";

import "./Dashboard.css";
import Chart from "../chart/Chart";
import EventTable from "../chart/EventTable";
import EventChart from "../chart/EventChart";
import React, { useEffect, useState, useRef } from "react";
import {
  getMerticTimepoints,
  getRawTimeseries,
  getEvents,
  getUserIds,
  calculateMetrics,
} from "../../utils/api";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import DashboardHeader from "../dashboard-header/DashboardHeader";
import { ChartStore } from "../../store/chartStore";
import { Button, Spin, notification } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

const antIcon = (
  <LoadingOutlined style={{ fontSize: 24, color: "#24265a" }} spin />
);

function Dashboard({ store, navStore, authStore }) {
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [userid, setUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [headerChange, setHeaderChange] = useState(false);
  const [selected, setSelected] = useState(null);
  const [heartRates, setHeartRates] = useState([]);
  const [sdnn, setSdnn] = useState([]);

  const height = 270;
  const width = 500;
  const data2 = [
    {
      id: "stress_index",
      title: "Stress Index",
      series: [
        { id: "stress_index", title: "Stress Index" },
        { id: "mean_hr", title: "Mean Heart Rate" },
      ],
    },
    {
      id: "sdnn",
      title: "Standard Deviation of NN",
      series: [{ id: "sdnn", title: "Standard Deviation of NN" }],
    },

    {
      id: "sd_ratio",
      title: "SD Ratio",
      series: [
        { id: "sd_ratio", title: "SD Ratio" },
        { id: "mean_hr", title: "Mean Heart Rate" },
      ],
    },
    {
      id: "triangular_index",
      title: "HRV Triangular Index",
      series: [{ id: "triangular_index", title: "HRV Triangular Index" }],
    },

    {
      id: "max_hr",
      title: "Max Heart Rate",
      series: [
        { id: "max_hr", title: "Max Heart Rate" },
        { id: "mean_hr", title: "Mean Heart Rate" },
      ],
    },
  ];

  useEffect(() => {
    navStore.selectedKey = "1";
    const fetchMyApi = async () => {
      if (userid !== "" && startDate !== "" && endDate !== "" && headerChange) {
        setDataLoading(true);
        const isoStart = moment(startDate).toDate().toISOString();
        const isoEnd = moment(endDate).add(1, "day").toDate().toISOString();

        let timeseries = getMerticTimepoints(userid, isoStart, isoEnd);
        let rawTimeseries = getRawTimeseries(userid, isoStart, isoEnd);
        let events_ = getEvents(userid, isoStart, isoEnd);
        store.timeseries = (await timeseries).data;
        store.rawTimeseries = (await rawTimeseries).data;
        store.events = (await events_).data;
        setHeaderChange(false);
        setDataLoading(false);
      }
      if (store.userids && store.userids.length === 0) {
        let userids_ = await getUserIds();
        store.userids = userids_.data.map((x) => x.userid).sort();
        store.useridsWithDateRange = userids_.data;
      }
      if (store.timeseries && store.timeseries.length > 0) {
        const x_ = d3
          .scaleTime()
          .range([0, width - 30])
          .domain(d3.extent(store.timeseries, (d) => new Date(d.timepoint)));
        store.x = () => x_;
      }
      setLoading(false);
    };
    fetchMyApi();
  }, [userid, endDate, startDate, headerChange, store]);

  const selectEvent = (selected, event) => {
    setSelected(selected);
    if (selected) {
      updateBounds([
        moment(event.startDate).add(-5, "minutes").toDate(),
        moment(event.endDate).add(5, "minutes").toDate(),
      ]);
    } else if (store.bounds && store.bounds[0] && store.bounds[1]) {
      updateBounds(store.bounds);
    } else {
      const domain = d3.extent(
        store.filteredEvents,
        (d) => new Date(d.eventstart)
      );
      updateBounds(domain);
    }
  };

  const updateBounds = (bounds) => {
    const x_ = d3
      .scaleTime()
      .range([0, width - 30])
      .domain(bounds);
    store.x = () => x_;
    store.x = () => x_;
  };

  const isDisabled = () => {
    if (!store.x()) {
      return true;
    }
    const domain = store.x().domain();
    return domain[1].getTime() - domain[0].getTime() > 24 * 60 * 60 * 1000;
  };
  const onCalculateClick = async () => {
    setCalculating(true);
    const domain = store.x().domain();
    try {
      await calculateMetrics(
        userid,
        domain[0].toISOString(),
        domain[1].toISOString()
      );

      const isoStart = moment(startDate).toDate().toISOString();
      const isoEnd = moment(endDate).add(1, "day").toDate().toISOString();

      let timeseries = getMerticTimepoints(userid, isoStart, isoEnd);
      store.timeseries = (await timeseries).data;
      notification.open({
        message: "Metrics Computed",
        description: "Metrics computed successfully",
      });
    } catch (e) {
      console.log(e);
    } finally {
      setCalculating(false);
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="Dashboard">
          <DashboardHeader
            userIds={store.userids}
            setUserId={setUserId}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setHeaderChange={setHeaderChange}
            authStore={authStore}
            store={store}
          />
          {!dataLoading && userid && (
            <React.Fragment>
              {store.events && store.events.length > 0 && (
                <EventChart
                  authStore={authStore}
                  navStore={navStore}
                  store={ChartStore}
                  userId={userid}
                  startDate={startDate}
                  endDate={endDate}
                  selected={selected}
                  setSelected={selectEvent}
                  updateBounds={updateBounds}
                />
              )}
              {store.events &&
                store.events.length > 0 &&
                !store.hideEventTable && (
                  <EventTable
                    authStore={authStore}
                    navStore={navStore}
                    store={ChartStore}
                    userId={userid}
                    startDate={startDate}
                    bounds={store.bounds}
                    endDate={endDate}
                    selected={selected}
                    setSelected={selectEvent}
                  />
                )}
            </React.Fragment>
          )}
          {dataLoading ? (
            <LoadingSpinner />
          ) : store.filteredData && store.filteredData.length > 0 ? (
            <div className="charts">
              <Chart
                key={`dashboard-chart-heart_rate`}
                store={ChartStore}
                rects={store.rects}
                data={store.rawTimeseriesTransformed}
                id={"heart_rate"}
                width={width}
                height={height}
                events={store.filteredEvents}
                title={"Heart Rate"}
                series={[{ id: "heart_rate", title: "Heart Rate" }]}
                lineValue={store.computedClosestValues[`heart_rate`]}
                selected={selected}
                bounds={store.bounds}
                setSelected={selectEvent}
              />
              {data2.map((d) => {
                return (
                  <Chart
                    key={`dashboard-chart-${d.id}`}
                    store={ChartStore}
                    rects={store.rects}
                    data={store.filteredData}
                    id={d.id}
                    width={width}
                    height={height}
                    events={store.filteredEvents}
                    title={d.title}
                    series={d.series}
                    lineValue={store.computedClosestValues[`${d.id}`]}
                    selected={selected}
                    bounds={store.bounds}
                    setSelected={selectEvent}
                  />
                );
              })}
            </div>
          ) : (
            <div className="charts-text">
              <p>Data not available for the specified filters.</p>
            </div>
          )}
          <div className="Dashboard__actionbar">
            <Button
              disabled={calculating || isDisabled()}
              onClick={onCalculateClick}
              size="large"
              type="primary"
            >
              {calculating && <Spin indicator={antIcon} />}
              {!calculating && <span> Calculate Metrics</span>}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default observer(Dashboard);
